import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Server, Globe, Shield, Book, FileText, Eye } from 'lucide-react';
import { getStorageUrl } from '../../utils/imageUrlUpdater';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVPSDropdownOpen, setIsVPSDropdownOpen] = useState(false);
  const [isCloudVPSOpen, setIsCloudVPSOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const vpsRegions = [
    { name: 'VPS Việt Nam', path: '/vps-viet-nam', icon: Globe },
    { name: 'VPS Singapore', path: '/vps-singapore', icon: Globe },
    { name: 'VPS US', path: '/vps-us', icon: Globe },
    { name: 'VPS Canada', path: '/vps-canada', icon: Globe },
    { name: 'VPS Châu Âu', path: '/vps-chau-au', icon: Globe },
    { name: 'VPS Australia', path: '/vps-australia', icon: Globe },
  ];

  const vpsTypes = [
    { name: 'VPS Windows', path: '/vps-windows', icon: Server },
    { name: 'VPS Forex', path: '/vps-forex', icon: Shield },
  ];

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={getStorageUrl('logo/logovmon.png')} alt="VMON" className="h-8 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Trang chủ
            </Link>

            {/* VPS Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsVPSDropdownOpen(!isVPSDropdownOpen)}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
              >
                Cloud VPS
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              
              {isVPSDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Theo khu vực
                    </div>
                    {vpsRegions.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        onClick={() => setIsVPSDropdownOpen(false)}
                      >
                        <item.icon className="mr-3 h-4 w-4" />
                        {item.name}
                      </Link>
                    ))}
                    <div className="border-t border-gray-100 my-2"></div>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Theo loại
                    </div>
                    {vpsTypes.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        onClick={() => setIsVPSDropdownOpen(false)}
                      >
                        <item.icon className="mr-3 h-4 w-4" />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/proxy-private"
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/proxy-private') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <Eye className="mr-2 h-4 w-4" />
              Proxy Private
            </Link>


            <Link
              to="/blog"
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/blog') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <Book className="mr-2 h-4 w-4" />
              Blog
            </Link>

            <Link
              to="/docs"
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/docs') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <FileText className="mr-2 h-4 w-4" />
              Tài liệu
            </Link>

            <Link
              to="/lien-he"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/lien-he') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Liên hệ
            </Link>
          </nav>

          {/* Login Button - Desktop */}
          <div className="hidden md:block">
            <a
              href="https://cloud.vmon.vn/"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Đăng Nhập
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 z-50 relative"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <>
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden" 
              onClick={() => setIsMobileMenuOpen(false)}
            />
            
            {/* Mobile Menu */}
            <div className="absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 md:hidden max-h-[80vh] overflow-y-auto">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                <Link
                  to="/"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Trang chủ
                </Link>
                
                {/* Mobile Cloud VPS Dropdown */}
                <div className="px-3 py-2">
                  <button
                    onClick={() => setIsCloudVPSOpen(!isCloudVPSOpen)}
                    className="flex items-center justify-between w-full text-base font-medium text-gray-700 hover:text-blue-600"
                  >
                    <span>Cloud VPS</span>
                    <ChevronDown className={`h-5 w-5 transition-transform ${isCloudVPSOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isCloudVPSOpen && (
                    <div className="mt-2 space-y-1 pl-4">
                      <div className="py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        VPS theo khu vực
                      </div>
                      {vpsRegions.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </Link>
                      ))}
                      
                      <div className="py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        VPS theo loại
                      </div>
                      {vpsTypes.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link
                  to="/proxy-private"
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/proxy-private') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Eye className="mr-3 h-4 w-4" />
                  Proxy Private
                </Link>

                <Link
                  to="/blog"
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/blog') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Book className="mr-3 h-4 w-4" />
                  Blog
                </Link>

                <Link
                  to="/docs"
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/docs') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FileText className="mr-3 h-4 w-4" />
                  Tài liệu
                </Link>

                <Link
                  to="/lien-he"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive('/lien-he') 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:text-blue-600'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Liên hệ
                </Link>

                {/* Mobile Login Button */}
                <div className="mt-4 px-3 pb-3">
                  <a
                    href="https://cloud.vmon.vn/"
                    rel="noopener noreferrer"
                    className="block w-full text-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    Đăng Nhập
                  </a>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Desktop Overlay for dropdown */}
        {isVPSDropdownOpen && (
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-25 hidden md:block" 
            onClick={() => setIsVPSDropdownOpen(false)}
          />
        )}
      </div>
    </header>
  );
};

export default Header;